import type { AgentSandboxPagination } from '../../types/negotiationAgents';
import type { FlowValidationProblem } from '../../types/negotiationAgents';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AiModelInfo,
  FlowExecutionStatus,
  Flow,
  SandboxExecutionStep,
  SandboxMessage,
  type LinhaDeEnvio,
  WhatsappTemplateDetails,
  type ZapsignTemplateInfo,
} from '../../types/flows';
import {
  createFlow,
  fetchAiModels,
  fetchFlow,
  fetchFlows,
  fetchWhatsappTemplateDetails,
  fetchLinhasDeEnvio,
  fetchZapsignTemplateInputs,
  sandboxFetchMessages,
  sandboxReply,
  sandboxStart,
  updateFlow,
} from './actions';

interface FlowsState {
  flows: Flow[];
  currentFlow: Flow | null;
  flowsById: Record<string, Flow>;
  flowLoadingById: Record<string, boolean>;
  flowErrorById: Record<string, string | null>;
  linhasDeEnvio: LinhaDeEnvio[];
  whatsappTemplateDetails: WhatsappTemplateDetails | null;
  whatsappTemplateDetailsLoading: boolean;
  zapsignTemplateInfo: ZapsignTemplateInfo | null;
  zapsignTemplateInfoLoading: boolean;
  aiModels: AiModelInfo[];
  aiModelsLoading: boolean;
  loading: boolean;
  saving: boolean;
  validationProblemsByFlow: Record<string, FlowValidationProblem>;
  sandboxExecutionId: string | null;
  sandboxStartRequestId: string | null;
  sandboxConversaId: string | null;
  sandboxStatus: FlowExecutionStatus | null;
  sandboxMessages: SandboxMessage[];
  sandboxMessagesPagination: AgentSandboxPagination;
  sandboxExecutionSteps: SandboxExecutionStep[];
  sandboxLoading: boolean;
  sandboxError: string | null;
}

const initialState: FlowsState = {
  flows: [],
  currentFlow: null,
  validationProblemsByFlow: {},
  flowsById: {},
  flowLoadingById: {},
  flowErrorById: {},
  loading: false,
  saving: false,
  linhasDeEnvio: [],
  whatsappTemplateDetails: null,
  whatsappTemplateDetailsLoading: false,
  zapsignTemplateInfo: null,
  zapsignTemplateInfoLoading: false,
  aiModels: [],
  aiModelsLoading: false,
  sandboxExecutionId: null,
  sandboxStartRequestId: null,
  sandboxConversaId: null,
  sandboxStatus: null,
  sandboxMessages: [],
  sandboxMessagesPagination: { page: 1, total: 0, hasMore: false },
  sandboxExecutionSteps: [],
  sandboxLoading: false,
  sandboxError: null,
};

const flowsSlice = createSlice({
  name: 'flows',
  initialState,
  reducers: {
    applyRestoredFlow(state, action: PayloadAction<Flow>) {
      state.currentFlow = action.payload;
      state.flowsById[action.payload.id] = action.payload;
      const index = state.flows.findIndex((flow) => flow.id === action.payload.id);
      if (index !== -1) state.flows[index] = action.payload;
      delete state.validationProblemsByFlow[action.payload.id];
    },
    setCurrentFlow: (state, action: PayloadAction<Flow | null>) => {
      state.currentFlow = action.payload;
    },
    resetFlowsState: () => initialState,
    resetSandbox: (state) => {
      state.sandboxStartRequestId = null;
      state.sandboxExecutionId = null;
      state.sandboxConversaId = null;
      state.sandboxStatus = null;
      state.sandboxMessages = [];
      state.sandboxMessagesPagination = { page: 1, total: 0, hasMore: false };
      state.sandboxExecutionSteps = [];
      state.sandboxLoading = false;
      state.sandboxError = null;
    },
    setSandboxMessages: (state, action: PayloadAction<SandboxMessage[]>) => {
      state.sandboxMessages = action.payload;
    },
    addSandboxMessage: (state, action: PayloadAction<SandboxMessage>) => {
      const exists = state.sandboxMessages.some((m) => m.id === action.payload.id);
      if (!exists) {
        state.sandboxMessages = [...state.sandboxMessages, action.payload].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFlows.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFlows.fulfilled, (state, action) => {
        state.loading = false;
        state.flows = action.payload;
      })
      .addCase(fetchFlows.rejected, (state) => {
        state.loading = false;
      })

      .addCase(fetchFlow.pending, (state, action) => {
        state.loading = true;
        state.flowLoadingById[action.meta.arg] = true;
        state.flowErrorById[action.meta.arg] = null;
      })
      .addCase(fetchFlow.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFlow = action.payload;
        state.flowsById[action.payload.id] = action.payload;
        state.flowLoadingById[action.payload.id] = false;
        state.flowErrorById[action.payload.id] = null;
      })
      .addCase(fetchFlow.rejected, (state, action) => {
        state.loading = false;
        state.flowLoadingById[action.meta.arg] = false;
        state.flowErrorById[action.meta.arg] =
          action.payload ?? action.error.message ?? 'Não foi possível carregar o fluxo.';
      })

      .addCase(createFlow.pending, (state) => {
        state.saving = true;
      })
      .addCase(createFlow.fulfilled, (state, action) => {
        state.saving = false;
        state.flows.push(action.payload);
        state.currentFlow = action.payload;
        state.flowsById[action.payload.id] = action.payload;
      })
      .addCase(createFlow.rejected, (state) => {
        state.saving = false;
      })

      .addCase(updateFlow.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateFlow.fulfilled, (state, action) => {
        state.saving = false;
        delete state.validationProblemsByFlow[action.payload.id];
        state.currentFlow = action.payload;
        state.flowsById[action.payload.id] = action.payload;
        const index = state.flows.findIndex((f) => f.id === action.payload.id);
        if (index !== -1) {
          state.flows[index] = action.payload;
        }
      })
      .addCase(updateFlow.rejected, (state, action) => {
        state.saving = false;
        if (action.payload) state.validationProblemsByFlow[action.meta.arg.flowId] = action.payload;
      })

      .addCase(fetchLinhasDeEnvio.fulfilled, (state, action) => {
        state.linhasDeEnvio = Array.isArray(action.payload) ? action.payload : [];
      })

      .addCase(fetchWhatsappTemplateDetails.pending, (state) => {
        state.whatsappTemplateDetailsLoading = true;
      })
      .addCase(fetchWhatsappTemplateDetails.fulfilled, (state, action) => {
        state.whatsappTemplateDetailsLoading = false;
        state.whatsappTemplateDetails = action.payload;
      })
      .addCase(fetchWhatsappTemplateDetails.rejected, (state) => {
        state.whatsappTemplateDetailsLoading = false;
      })

      .addCase(fetchZapsignTemplateInputs.pending, (state) => {
        state.zapsignTemplateInfoLoading = true;
        state.zapsignTemplateInfo = null;
      })
      .addCase(fetchZapsignTemplateInputs.fulfilled, (state, action) => {
        state.zapsignTemplateInfoLoading = false;
        state.zapsignTemplateInfo = action.payload;
      })
      .addCase(fetchZapsignTemplateInputs.rejected, (state) => {
        state.zapsignTemplateInfoLoading = false;
      })

      .addCase(sandboxStart.pending, (state, action) => {
        state.sandboxStartRequestId = action.meta.requestId;
        state.sandboxLoading = true;
        state.sandboxError = null;
        state.sandboxStatus = null;
        state.sandboxMessages = [];
        state.sandboxExecutionSteps = [];
      })
      .addCase(sandboxStart.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.sandboxStartRequestId) return;
        state.sandboxStartRequestId = null;
        state.sandboxLoading = false;
        state.sandboxExecutionId = action.payload.executionId;
        state.sandboxConversaId = action.payload.conversaId;
        state.sandboxStatus = action.payload.status;
        if (action.payload.executionPath?.length) {
          state.sandboxExecutionSteps = [
            ...state.sandboxExecutionSteps,
            ...action.payload.executionPath,
          ];
        }
      })
      .addCase(sandboxStart.rejected, (state, action) => {
        if (action.meta.requestId !== state.sandboxStartRequestId) return;
        state.sandboxStartRequestId = null;
        state.sandboxLoading = false;
        state.sandboxError = action.payload as string;
      })

      .addCase(sandboxReply.pending, (state, action) => {
        if (action.meta.arg.conversaId !== state.sandboxConversaId) return;
        state.sandboxLoading = true;
        state.sandboxError = null;
      })
      .addCase(sandboxReply.fulfilled, (state, action) => {
        if (action.meta.arg.conversaId !== state.sandboxConversaId) return;
        state.sandboxLoading = false;
        state.sandboxExecutionId = action.payload.executionId;
        state.sandboxStatus = action.payload.status;
        if (action.payload.executionPath?.length) {
          state.sandboxExecutionSteps = [
            ...state.sandboxExecutionSteps,
            ...action.payload.executionPath,
          ];
        }
      })
      .addCase(sandboxReply.rejected, (state, action) => {
        if (action.meta.arg.conversaId !== state.sandboxConversaId) return;
        state.sandboxLoading = false;
        state.sandboxError = action.payload as string;
      })

      .addCase(sandboxFetchMessages.fulfilled, (state, action) => {
        if (action.meta.arg.conversaId !== state.sandboxConversaId) return;
        state.sandboxMessagesPagination = {
          page: action.payload.page,
          total: action.payload.total,
          hasMore: action.payload.hasMore,
        };
        // Merge with existing (SSE) messages, deduplicating by id, sorted by createdAt
        const existingIds = new Set(state.sandboxMessages.map((m) => m.id));
        const newMsgs = action.payload.items.filter((m) => !existingIds.has(m.id));
        if (newMsgs.length > 0) {
          const merged = [...state.sandboxMessages, ...newMsgs];
          merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          state.sandboxMessages = merged;
        }
      })

      .addCase(fetchAiModels.pending, (state) => {
        state.aiModelsLoading = true;
      })
      .addCase(fetchAiModels.fulfilled, (state, action) => {
        state.aiModelsLoading = false;
        state.aiModels = action.payload;
      })
      .addCase(fetchAiModels.rejected, (state) => {
        state.aiModelsLoading = false;
      });
  },
});

export const { resetSandbox, addSandboxMessage, applyRestoredFlow } = flowsSlice.actions;
export const flowsReducer = flowsSlice.reducer;
